import React, { useState, useEffect } from 'react';
import '../../styles/admin/AdminProjects.css';

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    status: '',
    priority: '',
    featured: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [statistics, setStatistics] = useState(null);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const categories = [
    { value: '', label: 'Tất cả danh mục' },
    { value: 'web-development', label: 'Phát triển Web' },
    { value: 'mobile-app', label: 'Ứng dụng di động' },
    { value: 'architecture', label: 'Kiến trúc xây dựng' },
    { value: 'landscape', label: 'Tiểu cảnh sân vườn' },
    { value: 'solar', label: 'Năng lượng mặt trời' },
    { value: 'iot-cctv', label: 'IoT & Camera' }
  ];

  const statuses = [
    { value: '', label: 'Tất cả trạng thái' },
    { value: 'draft', label: 'Nháp' },
    { value: 'published', label: 'Đã xuất bản' },
    { value: 'archived', label: 'Lưu trữ' },
    { value: 'planning', label: 'Lên kế hoạch' },
    { value: 'in-progress', label: 'Đang thực hiện' },
    { value: 'completed', label: 'Hoàn thành' },
    { value: 'on-hold', label: 'Tạm dừng' }
  ];

  const priorities = [
    { value: '', label: 'Tất cả mức độ' },
    { value: 'low', label: 'Thấp' },
    { value: 'medium', label: 'Trung bình' },
    { value: 'high', label: 'Cao' },
    { value: 'urgent', label: 'Khẩn cấp' }
  ];

  useEffect(() => {
    fetchProjects();
    fetchStatistics();
  }, [currentPage, filters]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/api/admin/projects?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setProjects(data.data.projects || []);
        setTotalPages(data.data.totalPages || 1);
      } else {
        console.error('API returned error:', data.error);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        setStatistics(data.data);
      }
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSelectProject = (projectId) => {
    setSelectedProjects(prev => {
      if (prev.includes(projectId)) {
        return prev.filter(id => id !== projectId);
      } else {
        return [...prev, projectId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === projects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(projects.map(p => p.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.length === 0) return;
    
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedProjects.length} dự án đã chọn?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/bulk-delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ids: selectedProjects })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message);
        setSelectedProjects([]);
        fetchProjects();
        fetchStatistics();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bạn có chắc muốn xóa dự án này?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchProjects();
        fetchStatistics();
      } else {
        alert('Lỗi khi xóa dự án');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}/duplicate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok && data.success) {
        alert(data.message);
        fetchProjects();
        fetchStatistics();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error duplicating project:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleToggleFeatured = async (id, currentFeatured) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}/featured`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featured: !currentFeatured })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        fetchProjects();
        fetchStatistics();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Lỗi kết nối server');
    }
  };

  const handleUpdateProgress = async (id, currentProgress) => {
    const newProgress = prompt('Nhập tiến độ mới (0-100):', currentProgress);
    if (newProgress === null) return;

    const progressValue = parseInt(newProgress);
    if (isNaN(progressValue) || progressValue < 0 || progressValue > 100) {
      alert('Tiến độ phải là số từ 0 đến 100');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/projects/${id}/progress`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ progress: progressValue })
      });

      const data = await response.json();
      if (response.ok && data.success) {
        fetchProjects();
      } else {
        alert('Lỗi: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating progress:', error);
      alert('Lỗi kết nối server');
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'draft': 'Nháp',
      'published': 'Đã xuất bản',
      'archived': 'Lưu trữ',
      'planning': 'Lên kế hoạch',
      'in-progress': 'Đang thực hiện',
      'completed': 'Hoàn thành',
      'on-hold': 'Tạm dừng'
    };
    return statusMap[status] || status;
  };

  const getCategoryText = (category) => {
    const categoryMap = {
      'web-development': 'Phát triển Web',
      'mobile-app': 'Ứng dụng di động',
      'architecture': 'Kiến trúc xây dựng',
      'landscape': 'Tiểu cảnh sân vườn',
      'solar': 'Năng lượng mặt trời',
      'iot-cctv': 'IoT & Camera'
    };
    return categoryMap[category] || category;
  };

  const getPriorityText = (priority) => {
    const priorityMap = {
      'low': 'Thấp',
      'medium': 'Trung bình',
      'high': 'Cao',
      'urgent': 'Khẩn cấp'
    };
    return priorityMap[priority] || priority;
  };

  return (
    <div className="admin-projects">
      <div className="projects-header">
        <div className="header-content">
          <h1>🚀 Quản Lý Dự Án</h1>
          <p>Quản lý tất cả dự án của PhucAn Solutions</p>
          
          {/* Statistics Summary */}
          {statistics && (
            <div className="stats-summary">
              <div className="stat-item">
                <span className="stat-number">{statistics.overall.total_projects}</span>
                <span className="stat-label">Tổng dự án</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statistics.overall.featured_projects}</span>
                <span className="stat-label">Nổi bật</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{Math.round(statistics.overall.avg_progress || 0)}%</span>
                <span className="stat-label">Tiến độ TB</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{statistics.recent.recent_projects}</span>
                <span className="stat-label">Mới (30 ngày)</span>
              </div>
            </div>
          )}
        </div>
        <a href="/admin/projects/create" className="create-btn">
          ➕ Tạo Dự Án Mới
        </a>
      </div>

      {/* Filters */}
      <div className="projects-filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="🔍 Tìm kiếm dự án..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filters.category}
            onChange={(e) => handleFilterChange('category', e.target.value)}
            className="filter-select"
          >
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="filter-select"
          >
            {statuses.map(status => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.priority}
            onChange={(e) => handleFilterChange('priority', e.target.value)}
            className="filter-select"
          >
            {priorities.map(priority => (
              <option key={priority.value} value={priority.value}>
                {priority.label}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filters.featured}
            onChange={(e) => handleFilterChange('featured', e.target.value)}
            className="filter-select"
          >
            <option value="">Tất cả</option>
            <option value="true">Nổi bật</option>
            <option value="false">Không nổi bật</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedProjects.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-info">
            <span>Đã chọn {selectedProjects.length} dự án</span>
          </div>
          <div className="bulk-buttons">
            <button onClick={handleBulkDelete} className="bulk-delete-btn">
              🗑️ Xóa đã chọn
            </button>
            <button onClick={() => setSelectedProjects([])} className="bulk-clear-btn">
              ✖️ Bỏ chọn tất cả
            </button>
          </div>
        </div>
      )}

      {/* Projects Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner">🔄</div>
            <p>Đang tải dự án...</p>
          </div>
        ) : (
          <table className="projects-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={projects.length > 0 && selectedProjects.length === projects.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Hình ảnh</th>
                <th>Tiêu đề</th>
                <th>Danh mục</th>
                <th>Trạng thái</th>
                <th>Ưu tiên</th>
                <th>Tiến độ</th>
                <th>Nổi bật</th>
                <th>Ngày tạo</th>
                <th>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {projects.length > 0 ? (
                projects.map((project) => (
                  <tr key={project.id} className={selectedProjects.includes(project.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(project.id)}
                        onChange={() => handleSelectProject(project.id)}
                      />
                    </td>
                    <td>
                      <div className="project-image">
                        {project.image ? (
                          <img 
                            src={`${API_BASE_URL}${project.image}`}
                            alt={project.title}
                          />
                        ) : (
                          <div className="no-image">📷</div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="project-title">
                        <h4>{project.title}</h4>
                        <p>{project.description?.substring(0, 100) || 'Không có mô tả'}...</p>
                        {project.client_name && (
                          <small className="client-info">👤 {project.client_name}</small>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="category-badge">
                        {getCategoryText(project.category)}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${project.status}`}>
                        {getStatusText(project.status)}
                      </span>
                    </td>
                    <td>
                      <span className={`priority-badge ${project.priority}`}>
                        {getPriorityText(project.priority)}
                      </span>
                    </td>
                    <td>
                      <div 
                        className="progress-container clickable"
                        onClick={() => handleUpdateProgress(project.id, project.progress)}
                        title="Click để cập nhật tiến độ"
                      >
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{ width: `${project.progress || 0}%` }}
                          ></div>
                        </div>
                        <span className="progress-text">{project.progress || 0}%</span>
                      </div>
                    </td>
                    <td>
                      <button
                        onClick={() => handleToggleFeatured(project.id, project.featured)}
                        className={`featured-btn ${project.featured ? 'featured' : ''}`}
                        title={project.featured ? 'Bỏ nổi bật' : 'Đánh dấu nổi bật'}
                      >
                        {project.featured ? '⭐' : '☆'}
                      </button>
                    </td>
                    <td>
                      <span className="date">
                        {new Date(project.createdAt || project.created_at).toLocaleDateString('vi-VN')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <a 
                          href={`/admin/projects/edit/${project.id}`}
                          className="edit-btn"
                          title="Chỉnh sửa"
                        >
                          ✏️
                        </a>
                        <button
                          onClick={() => handleDuplicate(project.id)}
                          className="duplicate-btn"
                          title="Nhân bản"
                        >
                          📋
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="delete-btn"
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="empty-state">
                    <div className="empty-content">
                      <p>📭 Không có dự án nào phù hợp</p>
                      <a href="/admin/projects/create" className="create-link">
                        Tạo dự án đầu tiên
                      </a>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="page-btn"
          >
            ← Trước
          </button>
          
          <span className="page-info">
            Trang {currentPage} / {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="page-btn"
          >
            Sau →
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminProjects; 